/**
 * Calls for the P&ID screen, typed by the contract where the API declares a shape.
 *
 * The register rows are plain objects in the contract (the API builds them in
 * `solutions/obsolescence/pid/register.py`), so `RegisterTag` below names the fields this screen
 * reads. Upload is multipart, so it goes through `fetchOk`, the same request core with its cookie,
 * sign-in and error rules, without the JSON content type.
 */
import type { Schemas } from "@neio/contracts";
import { BASE, apiGet, apiSend, enc, fetchOk, path } from "../../lib/http";

export type PidStatus = Schemas["PidStatus"];
export type Sheet = Schemas["SheetView"];
export type SheetDetail = Schemas["SheetWithReadings"];
export type Reading = Schemas["ReadingView"];
export type UploadResult = Schemas["DocumentView"];
export type ImportResult = Schemas["ImportResult"];
export type ReviewAction = Schemas["ReviewBody"]["action"];

export interface TagOccurrence {
  reading_id: string;
  sheet_id: string;
  filename: string | null;
  page: number | null;
  box: number[] | null;
  raw: string | null;
  review_state: string | null;
  agreement: string | null;
}

export interface RegisterTag {
  tag: string;
  kind: string;
  status: "ok" | "ambiguous" | "unparsed";
  reasons: string[];
  reviewed: boolean;
  reviewed_status: string | null;
  from_range: boolean;
  occurrences: TagOccurrence[];
}

export interface Register {
  package: string;
  counts: Record<string, number>;
  tags: RegisterTag[];
}

export const pidApi = {
  status: () => apiGet("/api/v1/pid/status"),
  sheets: (pkg: string) => apiGet("/api/v1/pid/packages/{package}/sheets", { path: { package: pkg } }),
  sheet: (id: string) => apiGet("/api/v1/pid/sheets/{sheet_id}", { path: { sheet_id: id } }),
  register: async (pkg: string): Promise<Register> =>
    (await apiGet("/api/v1/pid/packages/{package}/register", { path: { package: pkg } })) as unknown as Register,
  find: async (pkg: string, q: string): Promise<RegisterTag[]> =>
    (await apiGet("/api/v1/pid/packages/{package}/find", { path: { package: pkg }, query: { q } })) as unknown as RegisterTag[],
  review: (reading: Reading, action: ReviewAction, value?: string, reason?: string) =>
    apiSend(
      "/api/v1/pid/readings/{reading_id}/review",
      { action, expected_version: reading.version, value: value || null, reason: reason || null },
      { path: { reading_id: reading.id } },
    ),
  readAgain: (sheetId: string) =>
    apiSend("/api/v1/pid/sheets/{sheet_id}/read", undefined, { path: { sheet_id: sheetId } }),
  importToRegister: (pkg: string) =>
    apiSend("/api/v1/pid/packages/{package}/register/import", undefined, { path: { package: pkg } }),

  /** Multipart upload of one PDF. A 401 sends the browser to sign in, like every other call. */
  async upload(pkg: string, file: File): Promise<UploadResult> {
    const form = new FormData();
    form.append("file", file, file.name);
    const res = await fetchOk(`${BASE}${path`/pid/packages/${pkg}/documents`}`, { method: "POST", body: form });
    return (await res.json()) as UploadResult;
  },
};

export const pidUrls = {
  image: (sheetId: string, width: number) => `${BASE}${path`/pid/sheets/${sheetId}/image`}?width=${enc(width)}`,
  crop: (readingId: string) => `${BASE}${path`/pid/readings/${readingId}/crop`}`,
  csv: (pkg: string) => `${BASE}${path`/pid/packages/${pkg}/register.csv`}`,
};
